import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, Save, Upload, X, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import RichTextEditor from "@/components/RichTextEditor";

// datetime-local requires "YYYY-MM-DDTHH:mm" (local, no tz)
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localInputToIso(local) {
  if (!local) return null;
  const d = new Date(local); // interpreted in local tz
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default function BlogEditor() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState({ title: "", content: "", excerpt: "", cover_file_id: null, publish_at_local: "" });
  const [cover, setCover] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [schedule, setSchedule] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    api.get(`/blog/${id}`)
      .then(({ data }) => {
        const scheduled = Boolean(data.is_scheduled);
        setForm({
          title: data.title,
          content: data.content,
          excerpt: data.excerpt || "",
          cover_file_id: data.cover_file_id || null,
          publish_at_local: isoToLocalInput(data.publish_at || data.published_at),
        });
        setSchedule(scheduled);
        setCover(data.cover);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const uploadCover = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCover(data);
      setForm((p) => ({ ...p, cover_file_id: data.id }));
      toast.success("Cover uploaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const removeCover = () => {
    setCover(null);
    setForm((p) => ({ ...p, cover_file_id: null }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.content || form.content === "<p></p>") {
      toast.error("Post content can't be empty");
      return;
    }
    setSubmitting(true);
    try {
      const publish_at = schedule && form.publish_at_local ? localInputToIso(form.publish_at_local) : null;
      const payload = {
        title: form.title,
        content: form.content,
        excerpt: form.excerpt || null,
        cover_file_id: form.cover_file_id,
        publish_at,
      };
      if (isEdit) {
        await api.patch(`/blog/${id}`, payload);
        toast.success(publish_at && new Date(publish_at) > new Date() ? "Post scheduled" : "Post updated");
      } else {
        await api.post("/blog", payload);
        toast.success(publish_at && new Date(publish_at) > new Date() ? "Post scheduled" : "Post published");
      }
      navigate("/staff", { replace: true, state: { tab: "blog", refreshedAt: Date.now() } });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#C8102E]" /></div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/staff" state={{ tab: "blog" }} className="inline-flex items-center gap-2 text-sm font-bold mb-6 hover:underline" data-testid="back-to-blog-admin">
          <ArrowLeft className="h-4 w-4" /> Back to blog
        </Link>

        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-6">
          {isEdit ? "Edit post" : "New blog post"}
        </h1>

        <form onSubmit={onSubmit} className="space-y-6 brut-card-static p-6 sm:p-8" data-testid="blog-form">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-bold mb-1.5">Title</label>
            <input
              required
              className="brut-input"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. 5 things Year 12s should do this term"
              data-testid="blog-title"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-bold mb-1.5">Excerpt (optional preview)</label>
            <input
              className="brut-input"
              value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              placeholder="One-line summary shown on the blog listing"
              maxLength={300}
              data-testid="blog-excerpt"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-bold mb-1.5">Cover image (optional)</label>
            {cover ? (
              <div className="flex items-center gap-3 border-2 border-black rounded-lg p-3 bg-zinc-50">
                <img src={`${API}/files/${cover.id}`} alt="" className="h-16 w-24 object-cover border-2 border-black rounded" />
                <span className="text-sm font-bold truncate flex-1">{cover.original_filename}</span>
                <button type="button" onClick={removeCover} className="text-zinc-700 hover:text-black p-1" data-testid="blog-remove-cover">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <label className="brut-btn-secondary cursor-pointer inline-flex" data-testid="blog-upload-cover">
                <Upload className="h-4 w-4" />
                {uploading ? "Uploading..." : "Add cover image"}
                <input
                  type="file"
                  accept="image/*"
                  disabled={uploading}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadCover(f); e.target.value = ""; }}
                  className="hidden"
                  data-testid="blog-cover-input"
                />
              </label>
            )}
            <p className="text-xs text-zinc-500 mt-2">Covers are auto-cropped to 16:9 on the blog. Landscape images work best.</p>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-bold mb-1.5">Content</label>
            <RichTextEditor
              value={form.content}
              onChange={(html) => setForm((p) => ({ ...p, content: html }))}
              placeholder="Write your post here — use the toolbar for headings, lists, links, and more."
            />
          </div>

          {/* Scheduling */}
          <div className="border-2 border-black rounded-lg p-4 bg-[#FEF3C7]">
            <label className="inline-flex items-center gap-2 cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={schedule}
                onChange={(e) => setSchedule(e.target.checked)}
                className="h-5 w-5 border-2 border-black accent-[#C8102E]"
                data-testid="blog-schedule-toggle"
              />
              <span className="text-sm font-bold flex items-center gap-1.5">
                <Clock className="h-4 w-4" />
                Schedule for a future date
              </span>
            </label>
            {schedule && (
              <div>
                <input
                  type="datetime-local"
                  required
                  className="brut-input"
                  value={form.publish_at_local}
                  onChange={(e) => setForm({ ...form, publish_at_local: e.target.value })}
                  data-testid="blog-schedule-datetime"
                />
                <p className="text-xs text-zinc-600 mt-2">
                  Post will remain hidden from students until this time, then publish automatically.
                </p>
              </div>
            )}
            {!schedule && (
              <p className="text-xs text-zinc-600 mt-1">Publishing immediately when you click below.</p>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={submitting} className="brut-btn-primary flex-1" data-testid="blog-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                <><Save className="h-4 w-4" /> {isEdit ? "Save changes" : (schedule ? "Schedule post" : "Publish post")}</>
              )}
            </button>
            <Link to="/staff" state={{ tab: "blog" }} className="brut-btn-secondary" data-testid="blog-cancel">Cancel</Link>
          </div>
        </form>
      </div>
    </Layout>
  );
}
